import React, { useCallback, useMemo, memo, useState, useEffect, useRef } from 'react'
import {
    Counter,
    LeftArrowContainer,
    RightArrowContainer,
    StyledImage,
    StyledImageContainer,
    StyledCarouselWrapper,
    BgPictureContainer,
    VirtualTourWrapper,
} from './styles'
import { Slider, Slide, DotGroup } from 'pure-react-carousel'
import type { GalleryProps } from './types'
import { LeftNavigator, RightNavigator } from '../icons'
import CarouselDots from './carousel-dots'
import OverlayModal from './overlay-modal'
import { useCarousel } from './hooks/use-carousel'
import FullScreenModal from './full-screen-modal'
import { getImageUrl } from './get-image-url'
import VirtualTourTag from '../../../common/virtual-tour-tag'
import { VirtualTourPosition } from '../../constants'
import { pageTypeEventCategories } from '../../../../services/analytics-service/categories'

const GalleryCarousel: React.FC<GalleryProps & { parentRef: any }> = memo(
    ({
        images,
        video,
        skipFullScreen = false,
        skipOverlay = false,
        imageBorder,
        imageBorderRadius,
        onSlideChange,
        parentRef,
        showDots,
        showCountTile,
        showArrows,
        footer,
        header,
        autoPlay,
        onGalleryImageClick,
        onFullscreenImageClick,
        onFullScreenOpen,
        onOverlayOpen,
        layout,
    }) => {
        const [isFullScreen, setFullScreen] = useState(false)
        const [isOverlayOpen, setOverlay] = useState(false)
        const [shouldAutoplayVideo, setShouldAutoplayVideo] = useState(false)
        const isDraggingRef = useRef(false)
        const dragStartPos = useRef({ x: 0, y: 0 })

        const { currentSlide, prev, next, goToSlide, resumeAutoplay, stopAutoplay } = useCarousel(parentRef, {
            onSlideChange,
        })

        useEffect(() => {
            if (!autoPlay) {
                return
            }
            if (isOverlayOpen || isFullScreen) {
                stopAutoplay()
            } else {
                resumeAutoplay()
            }
        }, [isOverlayOpen, isFullScreen, autoPlay, stopAutoplay, resumeAutoplay])

        const handleOnItemClick = useCallback(() => {
            // Track image click
            onGalleryImageClick?.({
                imageIndex: currentSlide,
                layout: layout!,
            })

            // Autoplay video only if clicking on index 0 (video position)
            setShouldAutoplayVideo(currentSlide === 0)

            if (!skipFullScreen) {
                setFullScreen(true)
            } else if (!skipOverlay) {
                setOverlay(true)
            }
        }, [skipFullScreen, skipOverlay, onGalleryImageClick, currentSlide, layout])

        const handleMouseDown = useCallback(event => {
            dragStartPos.current = { x: event.clientX, y: event.clientY }
            isDraggingRef.current = false
        }, [])

        const handleMouseMove = useCallback(event => {
            const dx = event.clientX - dragStartPos.current.x
            const dy = event.clientY - dragStartPos.current.y
            if (!isDraggingRef.current && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
                isDraggingRef.current = true
            }
        }, [])

        const handleMouseEnter = useCallback(() => {
            stopAutoplay()
        }, [stopAutoplay])

        const handleMouseLeave = useCallback(() => {
            resumeAutoplay()
        }, [resumeAutoplay])

        const handleMouseUp = useCallback(() => {
            if (!isDraggingRef.current) {
                handleOnItemClick()
            }
            isDraggingRef.current = false
        }, [handleOnItemClick])

        const imageUrlsMap = useMemo(
            () =>
                images.map(image => ({
                    small: getImageUrl('small-mobile', image),
                    medium: getImageUrl('medium-mobile', image),
                    large: getImageUrl('large-mobile', image),
                    desktop: getImageUrl('desktop', image),
                    wideScreen: getImageUrl('wide-screen', image),
                })),
            [images],
        )

        const renderedImages = useMemo(
            () =>
                images.map((image, index) => {
                    const imageUrls = imageUrlsMap[index]

                    return (
                        <StyledImageContainer
                            key={`image_carousel_${index}`}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            imageBorder={imageBorder}
                            imageBorderRadius={imageBorderRadius}
                        >
                            <BgPictureContainer>
                                {!!imageUrls.wideScreen && (
                                    <source media="(min-width:1024px)" srcSet={imageUrls.wideScreen} />
                                )}
                                {!!imageUrls.desktop && <source media="(min-width:640px)" srcSet={imageUrls.desktop} />}
                                {!!imageUrls.large && <source media="(min-width:480px)" srcSet={imageUrls.large} />}
                                {!!imageUrls.medium && <source media="(min-width:320px)" srcSet={imageUrls.medium} />}
                                {!!imageUrls.small && (
                                    <StyledImage
                                        alt={image.caption}
                                        src={imageUrls.small}
                                        imageBorder={imageBorder}
                                        imageBorderRadius={imageBorderRadius}
                                        loading={index < 1 ? 'eager' : 'lazy'}
                                        decoding={index < 1 ? 'sync' : 'async'}
                                        fetchpriority={index < 1 ? 'high' : 'low'}
                                    />
                                )}
                            </BgPictureContainer>
                        </StyledImageContainer>
                    )
                }),
            [
                images,
                imageUrlsMap,
                handleMouseDown,
                handleMouseMove,
                handleMouseUp,
                handleMouseEnter,
                handleMouseLeave,
                imageBorder,
                imageBorderRadius,
            ],
        )

        const totalSlides = video ? images.length + 1 : images.length

        return (
            <StyledCarouselWrapper>
                <Slider>
                    {!!video && (
                        <Slide index={0} key="slide_0">
                            <StyledImageContainer
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                imageBorder={imageBorder}
                                imageBorderRadius={imageBorderRadius}
                            >
                                <BgPictureContainer>
                                    <StyledImage
                                        alt={video.caption}
                                        src={video.thumbnail || video.src}
                                        imageBorder={imageBorder}
                                        imageBorderRadius={imageBorderRadius}
                                        loading="eager"
                                        decoding="sync"
                                        fetchpriority="high"
                                    />
                                </BgPictureContainer>
                                <VirtualTourWrapper>
                                    <VirtualTourTag
                                        analyticsCategory={pageTypeEventCategories.PROPERTY_DETAILS_PAGE}
                                        analyticsLabel={VirtualTourPosition.PDP_GALLERY}
                                    />
                                </VirtualTourWrapper>
                            </StyledImageContainer>
                        </Slide>
                    )}
                    {renderedImages.map((renderedImage, index) => (
                        <Slide index={video ? index + 1 : index} key={`slide_${video ? index + 1 : index}`}>
                            {renderedImage}
                        </Slide>
                    ))}
                </Slider>
                {!!showDots && (
                    <DotGroup
                        renderDots={({ currentSlide, totalSlides }) => (
                            <CarouselDots currentIndex={currentSlide} totalImages={totalSlides} />
                        )}
                    />
                )}
                {!!showArrows && (
                    <>
                        <LeftArrowContainer onClick={prev}>
                            <LeftNavigator />
                        </LeftArrowContainer>
                        <RightArrowContainer onClick={next}>
                            <RightNavigator />
                        </RightArrowContainer>
                    </>
                )}
                {!!showCountTile && <Counter>{`${currentSlide + 1} / ${totalSlides}`}</Counter>}

                <FullScreenModal
                    images={images}
                    video={video}
                    isFullScreenOpen={isFullScreen}
                    isOverlayOpen={isOverlayOpen}
                    setFullScreen={setFullScreen}
                    header={header}
                    footer={footer}
                    setOverlay={setOverlay}
                    setSelectedImage={goToSlide}
                    currentIndex={currentSlide}
                    onFullscreenImageClick={onFullscreenImageClick}
                    onFullScreenOpen={onFullScreenOpen}
                    shouldAutoplayVideo={shouldAutoplayVideo}
                />

                <OverlayModal
                    isOverlayOpen={isOverlayOpen}
                    setOverlay={setOverlay}
                    images={images}
                    video={video}
                    initialSlide={currentSlide}
                    setSelectedImage={goToSlide}
                    footer={footer}
                    onOverlayOpen={onOverlayOpen}
                    shouldAutoplayVideo={shouldAutoplayVideo}
                />
            </StyledCarouselWrapper>
        )
    },
)

export default GalleryCarousel
